import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquare, Send } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { fmtDateTimeMty } from '@/lib/format';
import type { BookingComentario } from '@/lib/types';

/**
 * Hilo de comentarios de un booking, como conversación de chat (autor + fecha).
 * Reutilizable desde el Board y el reporte de Bookings.
 */
export function ComentariosDialog({
  open,
  onOpenChange,
  bookingId,
  bookingIdExterno,
  cliente,
  readOnly = false,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  bookingId: number;
  bookingIdExterno: string;
  cliente?: string;
  /** Si true, solo lectura (no muestra el input para agregar). */
  readOnly?: boolean;
}) {
  const qc = useQueryClient();
  const [texto, setTexto] = useState('');

  const q = useQuery({
    queryKey: ['booking-comentarios', bookingId],
    queryFn: () => api<{ comentarios: BookingComentario[] }>(`/bookings/${bookingId}/comentarios`),
    enabled: open,
  });

  const addMut = useMutation({
    mutationFn: (t: string) =>
      api<BookingComentario>(`/bookings/${bookingId}/comentarios`, {
        method: 'POST',
        body: JSON.stringify({ texto: t }),
      }),
    onSuccess: () => {
      setTexto('');
      qc.invalidateQueries({ queryKey: ['booking-comentarios', bookingId] });
      // Refresca el contador del botón en Board y reporte.
      qc.invalidateQueries({ queryKey: ['bookings'] });
      qc.invalidateQueries({ queryKey: ['reporte-bookings'] });
    },
    onError: (e: unknown) => toast.error(e instanceof ApiError ? e.message : 'No se pudo enviar'),
  });

  const enviar = () => {
    const t = texto.trim();
    if (!t) return;
    addMut.mutate(t);
  };

  const comentarios = q.data?.comentarios ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" /> Comentarios · {bookingIdExterno}
          </DialogTitle>
          <DialogDescription>
            {cliente ? `${cliente} · ` : ''}Conversación (más reciente arriba). Cada comentario
            guarda quién y cuándo.
          </DialogDescription>
        </DialogHeader>

        {/* Input para agregar (arriba, como se ve el más reciente primero) */}
        {!readOnly && (
          <div className="mt-2 flex items-end gap-2">
            <textarea
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  enviar();
                }
              }}
              placeholder="Escribe un comentario…  (Ctrl+Enter para enviar)"
              rows={2}
              maxLength={4000}
              className="flex-1 rounded-md border border-ink-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
            />
            <Button onClick={enviar} disabled={addMut.isPending || !texto.trim()}>
              <Send className="h-4 w-4" />
              {addMut.isPending ? 'Enviando…' : 'Enviar'}
            </Button>
          </div>
        )}

        <div className="mt-4 space-y-2 max-h-[55vh] overflow-y-auto pr-1">
          {q.isLoading && <p className="text-sm text-ink-400">Cargando…</p>}
          {!q.isLoading && comentarios.length === 0 && (
            <p className="text-sm text-ink-400 italic">Sin comentarios todavía.</p>
          )}
          {comentarios.map((c) => (
            <div key={c.id} className="rounded-md border border-ink-100 bg-surface-2 px-3 py-2">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-xs font-semibold text-ink-800">{c.autor?.nombre ?? 'Usuario'}</span>
                <span className="text-[11px] text-ink-400 tabular">{fmtDateTimeMty(c.createdAt)}</span>
              </div>
              <p className="mt-0.5 text-sm text-ink-700 whitespace-pre-wrap break-words">{c.texto}</p>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
